import React, { useMemo } from 'react';

import { Alert, Button, Stack } from '@mui/material';
import { Control } from 'react-hook-form';

import { ControlledRadioGroupField } from '../../../../../../components/FormControlsV2/ControlledRadioGroupField';
import { ControlledTextField } from '../../../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../../../components/FormControlsV2/FormProvider';
import { Position } from '../../../../../../types/position';
import { signalTypes } from '../../constants';
import { FormFields, SubmissionState } from '../../types';

const classNamesByTab: Record<number, Record<string, string>> = {
  1: {
    size: 'market-signal-size',
    stoploss: 'market-signal-stoploss',
    type: 'market-signal-type',
    comment: 'market-signal-comment',
    submit: 'market-signal-submit',
  },
  2: {
    size: 'delayed-signal-size',
    stoploss: 'delayed-signal-stoploss',
    price: 'delayed-signal-price',
    timeout: 'delayed-signal-timeout',
    type: 'delayed-signal-type',
    comment: 'delayed-signal-comment',
    submit: 'delayed-signal-submit',
  },
  3: {
    size: 'package-signal-size',
    stoploss: 'package-signal-stoploss',
    type: 'package-signal-type',
    comment: 'package-signal-comment',
    add: 'package-signal-add',
    submit: 'package-signal-submit',
  },
  4: {
    size: 'portfolio-signal-size',
    stoploss: 'portfolio-signal-stoploss',
    type: 'portfolio-signal-type',
    comment: 'portfolio-signal-comment',
    submit: 'portfolio-signal-submit',
  },
  5: {
    size: 'limit-order-size',
    price: 'limit-order-price',
    timeout: 'limit-order-timeout',
    type: 'limit-order-type',
    comment: 'limit-order-comment',
    submit: 'limit-order-submit',
  },
};

type Props = {
  control: Control<FormFields>;
  values: FormFields;
  errors: Record<string, unknown>;
  activeTabId: number;
  loading: boolean;
  details: {
    symbol: string;
    classCode: string;
    name: string;
  } | null;
  editablePosition: Position | null;
  error: string;
  limitOrderTimeoutMax: number;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onAddSignal: (values: FormFields) => void;
  onUpdatePortfolioPositionSignal: (values: FormFields) => void;
  // Для пакетной заявки
  packageSignalsCount?: number;
  onSubmitPackageSignals?: () => void;
  // Для портфельного сигнала
  hasPortfolioChanges?: boolean;
  onSavePortfolioChanges?: () => void;
  submissionState: SubmissionState;
  onAllowRetry: () => void;
};

export const Form: React.FC<Props> = ({
  control,
  values,
  errors,
  activeTabId,
  loading,
  details,
  editablePosition,
  error,
  limitOrderTimeoutMax,
  onSubmit,
  onAddSignal,
  onUpdatePortfolioPositionSignal,
  packageSignalsCount = 0,
  onSubmitPackageSignals,
  hasPortfolioChanges = false,
  onSavePortfolioChanges,
  submissionState,
  onAllowRetry,
}) => {
  const isCloseSignal = Number(values.type) === 3;
  const isSubmissionBlocked = loading || submissionState !== 'idle';
  const isDisabled = isSubmissionBlocked || !details || isCloseSignal;

  const cls = useMemo(() => classNamesByTab[activeTabId] || {}, [activeTabId]);

  return (
    <form noValidate onSubmit={onSubmit}>
      <FormProvider control={control} errors={errors}>
        <Stack direction="row" gap={2}>
          <ControlledTextField
            name="size"
            label="Объем позиции, %"
            disabled={isDisabled}
            type="text"
            className={cls.size}
          />
          <ControlledTextField
            name="stoploss"
            label="Уровень Stop Loss"
            disabled={isDisabled}
            type="text"
            className={cls.stoploss}
          />
        </Stack>

        <Stack direction="row" gap={2}>
          {(activeTabId === 5 || activeTabId === 2) && (
            <ControlledTextField
              name="price"
              label="Цена"
              disabled={isSubmissionBlocked || !details}
              type="text"
              className={cls.price}
            />
          )}

          <ControlledTextField name="takeprofit" label="Уровень Take Profit" disabled={isDisabled} type="text" />
        </Stack>

        {(activeTabId === 5 || activeTabId === 2) && (
          <ControlledTextField
            name="timeout"
            label={activeTabId === 5 ? 'Время действия(минуты)' : 'Время действия после достижения цены(минуты)'}
            type="number"
            disabled={isSubmissionBlocked || !details}
            className={cls.timeout}
            inputProps={activeTabId === 5 ? { max: limitOrderTimeoutMax } : { max: 40 }}
          />
        )}

        <Stack direction="row" gap={2} className={cls.type}>
          <ControlledRadioGroupField
            name="type"
            label="Тип сигнала"
            options={signalTypes.filter((item) => !(activeTabId === 4 && item.value === '3'))}
            disabled={isSubmissionBlocked || !details}
            row
          />
        </Stack>

        <ControlledTextField
          name="description"
          label="Комментарий к сигналу"
          rows={5}
          multiline
          disabled={loading || !details}
          className={cls.comment}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {submissionState === 'unknown' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Не удалось подтвердить результат подачи сигнала. Сигнал мог быть принят сервером.
            <Button type="button" size="small" sx={{ mt: 1 }} onClick={onAllowRetry}>
              Разрешить повторную подачу
            </Button>
          </Alert>
        )}

        {(activeTabId === 1 || activeTabId === 2 || activeTabId === 5) && (
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            disabled={isSubmissionBlocked}
            className={cls.submit}
          >
            Сформировать сигнал
          </Button>
        )}

        {activeTabId === 3 && (
          <Stack direction="row" gap={2}>
            <Button
              type="button"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={isSubmissionBlocked || !details || Object.keys(errors).length > 0}
              onClick={() => onAddSignal(values)}
              className={cls.add}
            >
              Добавить в список
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={isSubmissionBlocked || packageSignalsCount === 0}
              onClick={() => onSubmitPackageSignals && onSubmitPackageSignals()}
              className={cls.submit}
            >
              Сформировать сигнал
            </Button>
          </Stack>
        )}

        {activeTabId === 4 && (
          <>
            <Stack direction="row" gap={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                disabled={isSubmissionBlocked || !details || editablePosition !== null}
              >
                Добавить в список
              </Button>

              <Button
                type="button"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                disabled={isSubmissionBlocked || !details || !editablePosition || Object.keys(errors).length > 0}
                onClick={() => onUpdatePortfolioPositionSignal(values)}
              >
                Внести изменения
              </Button>
            </Stack>
            <Button
              type="button"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={isSubmissionBlocked || !hasPortfolioChanges}
              onClick={() => onSavePortfolioChanges && onSavePortfolioChanges()}
              className={cls.submit}
            >
              Сохранить изменения
            </Button>
          </>
        )}
      </FormProvider>
    </form>
  );
};
